/**
 * AuthLayout — the shared centred frame for sheets 06 and 07a–d (A.1, A.4,
 * A.5, SEC.7; NFR-008): wordmark, one card, a footer that carries the 12 h
 * idle notice where sheet 07 states it (callout 10). Wraps `DesktopOnly`, so
 * below 768 px every auth screen shows the notice, not a degraded layout
 * (CLAUDE.md §5).
 *
 * Also home to the three small form pieces every auth screen shares, so the
 * copy and the tokens live in one place: `FieldError` (§7.2 invalid helper),
 * `DisabledReason` (§7.1: a disabled control always states its reason) and
 * `failureCopy` (the generic, non-revealing failure sentences; the 503
 * `try-again` problem gets its own wording).
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { PROBLEM, isProblem } from '@/lib/auth'
import { buildLabel } from '@/lib/build-info'
import { Card } from '@/components/ui/card'
import { DesktopOnly } from '@/components/trellis/DesktopOnly'

/** Sheet 07 callout 10. The 12 h value is a provisional Platform decision; one string, one place. */
export const IDLE_NOTICE = 'For your security, you are logged out after 12 hours without activity.'

export interface AuthLayoutProps {
  title: string
  lede?: ReactNode
  /** Show the 12 h idle notice in the footer (login, MFA challenge). */
  idleNotice?: boolean
  /** Show the running build in the footer, for reference (login; SC-36402). */
  buildVersion?: boolean
  children: ReactNode
}

export function AuthLayout({ title, lede, idleNotice = false, buildVersion = false, children }: AuthLayoutProps) {
  return (
    <DesktopOnly>
      <div className="min-h-screen bg-navy-50 px-8 py-16">
        <main className="mx-auto flex w-full max-w-[480px] flex-col gap-6">
          <p className="text-overline uppercase text-navy-600">Trellis · by Bloom Credit</p>
          <Card className="flex flex-col gap-6">
            <header>
              <h1 className="text-h1 text-navy-950">{title}</h1>
              {lede !== undefined && <p className="mt-2 text-body text-navy-600">{lede}</p>}
            </header>
            {children}
          </Card>
        </main>
        <footer className="mx-auto mt-6 flex w-full max-w-[480px] flex-col gap-2 text-caption text-navy-600">
          {idleNotice && <p>{IDLE_NOTICE}</p>}
          <p>
            <Link to="/legal/terms" className="text-[var(--color-link)] hover:underline">
              Terms
            </Link>
            {' · '}
            <Link to="/legal/privacy" className="text-[var(--color-link)] hover:underline">
              Privacy
            </Link>
          </p>
          {/* A version and a hash: mono (CLAUDE.md §5). */}
          {buildVersion && <p className="font-mono">{buildLabel()}</p>}
        </footer>
      </div>
    </DesktopOnly>
  )
}

/** §7.2: helper text in error-700 at 12px, referenced by the input's aria-describedby. */
export function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-caption text-[var(--color-error-700)]">
      {children}
    </p>
  )
}

/** §7.1, §11: a disabled control is always paired with its reason. */
export function DisabledReason({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-caption text-navy-600">
      {children}
    </p>
  )
}

/**
 * Generic failure copy. Never the error string (a transport error may carry a
 * host or a stack) and never a hint about which part failed (SEC.7).
 */
export function failureCopy(err: unknown): string {
  if (isProblem(err, PROBLEM.TRY_AGAIN)) return 'Please try again in a moment.'
  return 'Something went wrong. Please try again.'
}
